import React, { useState } from 'react';
import { UserProfile } from '../../types';
import { Bell, Save, Volume2, Vibrate, Smartphone } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
}

export const NotificationsSection: React.FC<Props> = ({ currentUser, onUpdateProfile }) => {
  const [notif, setNotif] = useState(currentUser.notificationSettings || {
    messages: true,
    groups: true,
    channels: true,
    calls: true,
    friendRequests: true,
    sound: true,
    vibrate: true,
    messagePreview: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ notificationSettings: notif });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const updateSettings = (key: keyof typeof notif, val: any) => {
    setNotif({ ...notif, [key]: val });
  };

  const renderToggle = (label: string, key: keyof typeof notif) => (
    <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
      <div className="text-sm font-semibold text-slate-200">{label}</div>
      <button
        onClick={() => updateSettings(key, !notif[key])}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notif[key] ? 'bg-amber-500' : 'bg-slate-700'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notif[key] ? 'translate-x-1' : '-translate-x-6'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-400" />
          الإشعارات
        </h3>

        <div className="space-y-3">
          {renderToggle('إشعارات الرسائل الخاصة', 'messages')}
          {renderToggle('إشعارات المجموعات', 'groups')}
          {renderToggle('إشعارات القنوات', 'channels')}
          {renderToggle('إشعارات المكالمات', 'calls')}
          {renderToggle('طلبات الصداقة والإضافة', 'friendRequests')}
          
          <div className="h-px w-full bg-slate-800/80 my-2"></div>
          
          {renderToggle('الصوت', 'sound')}
          {renderToggle('الاهتزاز', 'vibrate')}
          {renderToggle('معاينة الرسائل (على شاشة القفل)', 'messagePreview')}
        </div>

        <button disabled={isSaving} onClick={handleSave} className={`mt-4 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${savedSuccess ? 'bg-amber-400 text-slate-900' : 'bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-50'}`}>
          <Save className="w-4 h-4" />
          {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ إعدادات الإشعارات'}
        </button>
      </div>
    </div>
  );
};
